import React, { useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { TROVE_SKU_KEY } from './types';
import type { CustomFieldSelection, FieldDefinition, WizardState } from './types';

interface FieldsToUpdateStepProps {
  state: WizardState;
  updateState: (patch: Partial<WizardState>) => void;
  matchedCount: number | null;
  fields: FieldDefinition[];
  /**
   * External identifiers (e.g. UPC/EAN) — normally chosen separately on the Values step, but a
   * required identifier has nowhere else to go on a "create new records" template, which skips
   * Values entirely. Only the required ones are merged into this step's list (see `isCreate`
   * below); non-required identifiers stay exclusive to the Values step.
   */
  identifiers: FieldDefinition[];
  /** Whether to show the "Custom fields" section (`catalogTemplateDefinitions.additionalProperties`). */
  allowCustomFields: boolean;
}

let customFieldIdCounter = 0;
const nextCustomFieldId = () => `custom-${(customFieldIdCounter += 1)}`;

const FieldsToUpdateStep = ({
  state,
  updateState,
  matchedCount,
  fields,
  identifiers,
  allowCustomFields,
}: FieldsToUpdateStepProps) => {
  const isCreate = state.purpose === 'create';
  const requiredIdentifiers = isCreate ? identifiers.filter(d => d.required) : [];
  const identifierKeys = new Set(requiredIdentifiers.map(d => d.key));
  // Trove SKU is a locked, backend-mandated column on every "update" template (see
  // CreateCatalogUploadTemplateDialog's purpose-sync effect, which always keeps it in
  // `selectedFields`) — it's never a real choice, so it's left out of this list entirely rather
  // than shown as a checkbox the partner can't actually uncheck.
  const allEntries = [...fields, ...requiredIdentifiers]
    .filter(d => d.key !== TROVE_SKU_KEY)
    .sort((a, b) => a.label.localeCompare(b.label));

  // On a "create new records" template there's no existing record to match against, so the
  // partner's required columns — regular fields and identifiers alike — must be filled in
  // directly: pre-checked and locked. Trove SKU is always locked too (though never rendered —
  // see `allEntries` above) so that `lockedFieldKeys` below keeps it in `selectedFields` when the
  // partner clicks "None".
  const isLockedEntry = useCallback(
    (key: string): boolean =>
      key === TROVE_SKU_KEY || identifierKeys.has(key) || (isCreate && !!fields.find(f => f.key === key)?.required),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCreate, fields, identifiers],
  );

  const toggleEntry = useCallback(
    (key: string) => {
      if (isLockedEntry(key)) return;
      const current = state.selectedFields;
      const next = current.includes(key) ? current.filter(f => f !== key) : [...current, key];
      updateState({ selectedFields: next });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.selectedFields, updateState, isLockedEntry],
  );

  const isEntrySelected = (key: string): boolean =>
    isLockedEntry(key) || (identifierKeys.has(key) ? state.selectedIdentifiers.includes(key) : state.selectedFields.includes(key));

  const allEntryKeys = allEntries.map(d => d.key);
  const lockedFieldKeys = fields.filter(d => isLockedEntry(d.key)).map(d => d.key);
  const allSelected = allEntryKeys.length > 0 && allEntryKeys.every(isEntrySelected);

  // Required identifiers are always locked+selected (via `isLockedEntry`/the dialog's sync
  // effect), so "All"/"None" only ever needs to toggle the regular fields.
  const toggleAll = useCallback(() => {
    if (allSelected) {
      updateState({
        selectedFields: lockedFieldKeys,
      });
    } else {
      updateState({
        selectedFields: fields.map(d => d.key),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSelected, fields, updateState]);

  const handleAddCustomField = () => {
    const next: CustomFieldSelection = { id: nextCustomFieldId(), family: 'family', key: '' };
    updateState({ customFields: [...state.customFields, next] });
  };

  const handleCustomFieldChange = (id: string, patch: Partial<CustomFieldSelection>) => {
    updateState({
      customFields: state.customFields.map(c => (c.id === id ? { ...c, ...patch } : c)),
    });
  };

  const handleCustomFieldRemove = (id: string) => {
    updateState({ customFields: state.customFields.filter(c => c.id !== id) });
  };

  const namedCustomFieldCount = state.customFields.filter(c => c.key.trim()).length;
  const selectedCount = allEntryKeys.filter(isEntrySelected).length + namedCustomFieldCount;

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 0.5 }}>
        {isCreate ? 'Which fields would you like to include?' : 'Which fields would you like to update?'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {isCreate ?
          "Only the fields you select are included as columns in the template, so the file stays focused on what you're creating." :
          "Only the fields you select are included as columns in the template, so the file stays focused on what you're changing."}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {isCreate ? 'Fields to Include' : 'Fields to Update'}
          </Typography>
          <Link
            component="button"
            variant="body2"
            underline="hover"
            onClick={toggleAll}
            sx={{ fontWeight: allSelected ? 700 : 400 }}
          >
            All
          </Link>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', columnGap: 2 }}>
          {allEntries.map((entry) => {
            const isLocked = isLockedEntry(entry.key);
            return (
              <FormControlLabel
                key={entry.key}
                control={(
                  <Checkbox
                    size="small"
                    checked={isEntrySelected(entry.key)}
                    disabled={isLocked}
                    onChange={() => toggleEntry(entry.key)}
                    sx={{ p: 0.5 }}
                  />
                )}
                label={entry.label}
                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              />
            );
          })}
        </Box>
      </Paper>

      {allowCustomFields && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Custom fields</Typography>
            <Typography variant="body2" color="text.secondary">
              Add a custom family or data key to include as an extra column.
            </Typography>
            {state.customFields.map(custom => (
              <Box key={custom.id} sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id={`custom-field-source-${custom.id}`}>Source</InputLabel>
                  <Select
                    labelId={`custom-field-source-${custom.id}`}
                    label="Source"
                    value={custom.family}
                    onChange={(e: SelectChangeEvent) =>
                      handleCustomFieldChange(custom.id, { family: e.target.value as CustomFieldSelection['family'] })}
                  >
                    <MenuItem value="family">family</MenuItem>
                    <MenuItem value="data">data</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Data key"
                  value={custom.key}
                  onChange={e => handleCustomFieldChange(custom.id, { key: e.target.value })}
                />
                <IconButton size="small" onClick={() => handleCustomFieldRemove(custom.id)} sx={{ color: 'text.secondary' }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddCustomField}
            sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
          >
            Add custom field
          </Button>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Chip
          label={`${selectedCount} field${selectedCount === 1 ? '' : 's'} selected`}
          size="small"
        />
        {!isCreate && (
          <Chip label={matchedCount != null ? `${matchedCount} template rows` : '— template rows'} size="small" />
        )}
      </Box>
    </Box>
  );
};

export default FieldsToUpdateStep;
